import { compareAsc, compareDesc, parseISO } from 'date-fns';

import type { StorageRules } from '~/services/enterprise.storage.server';

export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

export const capitalizeFirstLetter = (string: string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export function getSearchParamsString(
  searchParams: URLSearchParams,
  changes: Record<string, string | number | undefined>,
) {
  const newSearchParams = new URLSearchParams(searchParams);

  for (const [key, value] of Object.entries(changes)) {
    if (value === undefined) {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, String(value));
    }
  }

  return newSearchParams.toString();
}

export function getProjectStorageTypeLabel(storageRules?: StorageRules): string {
  if (!storageRules) {
    return '';
  }

  const storageTypes = {
    'Cloud Sync': storageRules.enableCloudSync,
    'Local Vault': storageRules.enableLocalVault,
    'Git Sync': storageRules.enableGitSync,
  };

  const allowedStorageTypes = Object.entries(storageTypes)
    .filter(([, enabled]) => enabled)
    .map(([label]) => label);

  // Join with ", " but use "and" before the last item
  return allowedStorageTypes.length
    ? allowedStorageTypes.join(', ').replace(/, ([^,]+)$/, ' and $1')
    : 'No storage types selected';
}

export function round(num: number | string, decimals = 0) {
  return Number(Math.round(Number(num + 'e' + decimals)) + 'e-' + decimals);
}

export function diffInDayCeil(dateA: Date, dateB: Date) {
  const diffTime = dateA.getTime() - dateB.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function formatNumber(num: number) {
  return num.toString().replaceAll(/\B(?=(\d{3})+(?!\d)(?=\.\d*|$))/g, ',');
}

// Placeholder origin used only to resolve `url` for validation; never sent anywhere.
const REDIRECT_VALIDATION_BASE = 'http://internal.invalid';

export function isValidRedirect(url: string | null | undefined) {
  if (!url || !url.startsWith('/')) return false;

  // Prefix checks alone miss browser URL-normalization tricks (e.g. leading backslash);
  // resolving and comparing origins catches those too.
  return new URL(url, REDIRECT_VALIDATION_BASE).origin === REDIRECT_VALIDATION_BASE;
}

// Used only for the desktop app's OAuth relay, which intentionally redirects to an
// external authorization URL (e.g. a loopback address owned by the desktop client).
export function isValidExternalRedirect(url: string | null | undefined) {
  return Boolean(url && (url.startsWith('http:') || url.startsWith('https:')));
}

export function isFirebasePreview(hostname: string) {
  return hostname.includes('.web.app');
}
